import React, { useState, useEffect, useMemo } from 'react';
import { ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from 'recharts';
import '../styles/exchanges.css';

import api_url from "../config/api_url.json";

console.log('api_url config:', api_url);

const base_api_url = api_url.backend_api;

console.log('base_api_url:', base_api_url);


// Color palette for exchanges
const EXCHANGE_COLORS = {
  binance: '#F3BA2F',
  binance_futures: '#F3BA2F',
  okex: '#000000',
  okex_swap: '#000000',
  bybit: '#F7931A',
  bybit_spot: '#F7931A',
  gate: '#C247F6',
  gate_futures: '#C247F6',
  bitget: '#02e0ff',
  bitget_futures: '#02e0ff',
  bingx: '#0f5375',
  bingx_futures: '#0f5375',
  mxc: '#1890FF',
  mxc_futures: '#1890FF',
  crypto_com: '#0066CC',
  crypto_com_futures: '#0066CC',
  hyperliquid: '#00E5A0',
  hyperliquid_spot: '#00E5A0',
  kraken: '#5741D9',
  deribit: '#0a3033',
  dydx: '#6366F1',
  drift: '#ef59d6',
  binance_us: '#FFD700'
};

// Exchange name mapping for display
const EXCHANGE_DISPLAY_NAMES = {
  // Futures names
  binance_futures: 'Binance',
  okex_swap: 'OKX',
  bybit: 'Bybit',
  bitget_futures: 'Bitget',
  mxc_futures: 'MEXC',
  gate_futures: 'Gate',
  hyperliquid: 'Hyperliquid',
  bingx_futures: 'BingX',
  crypto_com_futures: 'crypto.com',
  kraken: 'Kraken',
  deribit: 'Deribit',
  dydx: 'dYdX',
  drift: 'Drift',
  // Spot names
  binance: 'Binance',
  gate: 'Gate',
  MXC: 'MEXC',
  mxc: 'MEXC',
  bitget: 'Bitget',
  bybit_spot: 'Bybit',
  okex: 'OKX',
  crypto_com: 'crypto.com',
  bingx: 'BingX',
  hyperliquid_spot: 'Hyperliquid',
  binance_us: 'Binance US'
};

// Get display name for exchange
const getExchangeDisplayName = (exchange) => {
  if (!exchange) return '';
  return EXCHANGE_DISPLAY_NAMES[exchange] || exchange.replace(/_/g, ' ');
};

// Format date to yyyy-mm-dd
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

// Format currency values
const formatCurrency = (value) => {
  const numValue = parseFloat(value);
  if (isNaN(numValue) || numValue === null || numValue === undefined) return '$0.00';
  if (numValue >= 1e9) return `$${(numValue / 1e9).toFixed(2)}B`;
  if (numValue >= 1e6) return `$${(numValue / 1e6).toFixed(2)}M`;
  if (numValue >= 1e3) return `$${(numValue / 1e3).toFixed(2)}K`;
  return `$${numValue.toFixed(2)}`;
};

// Format numbers
const formatNumber = (value) => {
  const numValue = parseFloat(value);
  if (isNaN(numValue) || numValue === null || numValue === undefined) return '0.00';
  if (numValue >= 1e9) return `${(numValue / 1e9).toFixed(2)}B`;
  if (numValue >= 1e6) return `${(numValue / 1e6).toFixed(2)}M`;
  if (numValue >= 1e3) return `${(numValue / 1e3).toFixed(2)}K`;
  return numValue.toFixed(2);
};

// Format percentage
const formatPercentage = (value) => {
  const numValue = parseFloat(value);
  if (isNaN(numValue) || numValue === null || numValue === undefined) return '0.00%';
  return `${numValue.toFixed(2)}%`;
};

// Parse formatted values back to numbers for sorting (handles K, M, B suffixes)
const parseFormattedValue = (formattedStr) => {
  if (!formattedStr || formattedStr === '') return 0;

  const str = formattedStr.toString().replace(/[$,]/g, ''); // Remove $ and commas
  const match = str.match(/^([0-9.-]+)([KMB%]?)$/i);

  if (!match) return 0;

  const number = parseFloat(match[1]);
  const suffix = match[2].toUpperCase();

  if (isNaN(number)) return 0;

  switch (suffix) {
    case 'K': return number * 1000;
    case 'M': return number * 1000000;
    case 'B': return number * 1000000000;
    case '%': return number; // For percentages, use the raw number
    default: return number;
  }
};

// Custom tooltip component
const CustomTooltip = ({ active, payload, label, chartType }) => {
  if (active && payload && payload.length) {
    return (
      <div className="exchanges-custom-tooltip">
        <p className="exchanges-tooltip-label">Date: {formatDate(label)}</p>
        {payload.map((entry, index) => (
          <p key={index} className="exchanges-tooltip-item" style={{ color: entry.color }}>
            {getExchangeDisplayName(entry.dataKey)}: {
              chartType === 'percent'
                ? formatPercentage(entry.value)
                : formatCurrency(entry.value)
            }
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Custom tooltip component for bar charts
const CustomBarTooltip = ({ active, payload, label, chartContext, totalValue }) => {
  if (active && payload && payload.length) {
    const value = payload[0].value;
    const percentage = totalValue > 0 ? (value / totalValue) * 100 : 0;
    const exchangeKey = payload[0].payload?.originalExchange || label.toLowerCase().replace(/\s+/g, '_');
    const exchangeColor = EXCHANGE_COLORS[exchangeKey] || '#8884d8';

    return (
      <div className="exchanges-custom-tooltip">
        <p className="exchanges-tooltip-label" style={{ color: exchangeColor, fontWeight: 'bold' }}>
          {label}
        </p>
        <p className="exchanges-tooltip-item" style={{ color: payload[0].color }}>
          {formatCurrency(value)} ({formatPercentage(percentage)} of {chartContext})
        </p>
      </div>
    );
  }
  return null;
};

// Custom label component for bar charts
const CustomLabel = (props) => {
  const { x, y, width, value } = props;
  const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;

  return (
    <text
      x={x + width / 2}
      y={y - 5}
      fill="#64748b"
      textAnchor="middle"
      fontSize="10"
      fontFamily="roboto mono, system-ui, sans-serif"
    >
      {formatCurrency(numValue)}
    </text>
  );
};

// Chart controls component
const ChartControls = ({ chartType, onChartTypeChange, timeframe, onTimeframeChange }) => (
  <div className="exchanges-chart-controls">
    <div className="exchanges-chart-type-buttons">
      <button
        className={`exchanges-control-button ${chartType === 'usd' ? 'active' : ''}`}
        onClick={() => onChartTypeChange('usd')}
      >
        USD
      </button>
      <button
        className={`exchanges-control-button ${chartType === 'line' ? 'active' : ''}`}
        onClick={() => onChartTypeChange('line')}
      >
        Line
      </button>
      <button
        className={`exchanges-control-button ${chartType === 'percent' ? 'active' : ''}`}
        onClick={() => onChartTypeChange('percent')}
      >
        %
      </button>
    </div>
    <div className="exchanges-timeframe-buttons">
      <button
        className={`exchanges-control-button ${timeframe === '7d' ? 'active' : ''}`}
        onClick={() => onTimeframeChange('7d')}
      >
        7d
      </button>
      <button
        className={`exchanges-control-button ${timeframe === '14d' ? 'active' : ''}`}
        onClick={() => onTimeframeChange('14d')}
      >
        14d
      </button>
      <button
        className={`exchanges-control-button ${timeframe === '30d' ? 'active' : ''}`}
        onClick={() => onTimeframeChange('30d')}
      >
        30d
      </button>
      <button
        className={`exchanges-control-button ${timeframe === '365d' ? 'active' : ''}`}
        onClick={() => onTimeframeChange('365d')}
      >
        365d
      </button>
    </div>
  </div>
);

// Interactive legend component - updated to grey out instead of hide
const InteractiveLegend = ({ items, selectedItems, onToggle, onToggleAll, allItems }) => (
  <div className="exchanges-interactive-legend">
    <button className="exchanges-legend-all-button" onClick={onToggleAll}>
      All
    </button>
    {allItems.map(item => {
      const isSelected = selectedItems.includes(item.key);
      return (
        <button
          key={item.key}
          className={`exchanges-legend-item ${isSelected ? 'active' : 'inactive'}`}
          style={{
            color: isSelected ? item.color : '#ccc',
            opacity: isSelected ? 1 : 0.5
          }}
          onClick={() => onToggle(item.key)}
        >
          {item.name}
        </button>
      );
    })}
  </div>
);

// Multi-select filter component for tables
const MultiSelectFilter = ({ data, onFilterChange, placeholder }) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  const allExchanges = useMemo(() => {
    const exchanges = [...new Set(data.map(item => item.exchange?.toLowerCase()))].filter(Boolean);
    // Create array of objects with both original and display names
    return exchanges.map(exchange => ({
      original: exchange,
      display: getExchangeDisplayName(exchange)
    })).sort((a, b) => a.display.localeCompare(b.display));
  }, [data]);

  useEffect(() => {
    if (inputValue.length > 0) {
      const filtered = allExchanges.filter(exchange =>
        exchange.display.toLowerCase().includes(inputValue.toLowerCase()) &&
        !selectedFilters.some(selected => selected.original === exchange.original)
      );
      setSuggestions(filtered.slice(0, 5));
    } else {
      setSuggestions([]);
    }
  }, [inputValue, allExchanges, selectedFilters]);

  useEffect(() => {
    // Pass only the original exchange names for filtering
    onFilterChange(selectedFilters.map(filter => filter.original));
  }, [selectedFilters, onFilterChange]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      const matchingExchange = allExchanges.find(exchange =>
        exchange.display.toLowerCase().includes(inputValue.toLowerCase().trim()) &&
        !selectedFilters.some(selected => selected.original === exchange.original)
      );

      if (matchingExchange) {
        setSelectedFilters([...selectedFilters, matchingExchange]);
        setInputValue('');
        setSuggestions([]);
      }
    }
  };

  const addFilter = (exchange) => {
    if (!selectedFilters.some(selected => selected.original === exchange.original)) {
      setSelectedFilters([...selectedFilters, exchange]);
      setInputValue('');
      setSuggestions([]);
    }
  };

  const removeFilter = (exchangeToRemove) => {
    setSelectedFilters(selectedFilters.filter(f => f.original !== exchangeToRemove.original));
  };

  return (
    <div className="exchanges-multi-select-filter">
      <div className="exchanges-filter-input-container">
        <input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="exchanges-table-filter"
        />
        {suggestions.length > 0 && (
          <div className="exchanges-filter-suggestions">
            {suggestions.map(suggestion => (
              <button
                key={suggestion.original}
                className="exchanges-filter-suggestion"
                onClick={() => addFilter(suggestion)}
              >
                {suggestion.display}
              </button>
            ))}
          </div>
        )}
      </div>
      {selectedFilters.length > 0 && (
        <div className="exchanges-selected-filters">
          {selectedFilters.map(filter => (
            <span key={filter.original} className="exchanges-filter-tag">
              {filter.display}
              <button
                className="exchanges-filter-remove"
                onClick={() => removeFilter(filter)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Data table component with multi-select filter and smooth transitions
const DataTable = ({ data, columns, title }) => {
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'desc' });
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);

  const filteredData = useMemo(() => {
    if (selectedFilters.length === 0) return data;
    return data.filter(row =>
      selectedFilters.some(filter =>
        row.exchange?.toLowerCase().includes(filter)
      )
    );
  }, [data, selectedFilters]);

  // Calculate dynamic totals based on filtered data
  const dynamicTotals = useMemo(() => {
    const totalVolume = filteredData.reduce((sum, item) => sum + (parseFloat(item.total_volume_usd) || 0), 0);
    const totalOI = filteredData.reduce((sum, item) => sum + (parseFloat(item.total_open_interest_usd) || 0), 0);
    return { totalVolume, totalOI };
  }, [filteredData]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      // Find the column definition for proper sorting
      const column = columns.find(col => col.key === sortConfig.key);

      let aValue, bValue;

      if (column && column.format) {
        // For computed columns (like percentages), use the formatted value for sorting
        // but extract numeric value for proper numeric sorting
        const aFormatted = column.format(a[sortConfig.key], a, dynamicTotals);
        const bFormatted = column.format(b[sortConfig.key], b, dynamicTotals);

        // Try to extract numeric values from formatted strings (e.g., "25.50%" -> 25.50)
        const aNumeric = parseFormattedValue(aFormatted);
        const bNumeric = parseFormattedValue(bFormatted);

        if (!isNaN(aNumeric) && !isNaN(bNumeric)) {
          return sortConfig.direction === 'asc' ? aNumeric - bNumeric : bNumeric - aNumeric;
        }

        aValue = aFormatted;
        bValue = bFormatted;
      } else {
        // For regular columns, use the raw value
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      }

      // Handle undefined/null values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
      if (bValue == null) return sortConfig.direction === 'asc' ? 1 : -1;

      // Numeric comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // String comparison
      const aStr = aValue.toString();
      const bStr = bValue.toString();
      return sortConfig.direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [filteredData, sortConfig, columns, dynamicTotals]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleFilterChange = (filters) => {
    setIsFiltering(true);
    setTimeout(() => {
      setSelectedFilters(filters);
      setIsFiltering(false);
    }, 30);
  };

  return (
    <div className="exchanges-table-container">
      <div className="exchanges-table-header">
        <h3>{title}</h3>
        <MultiSelectFilter
          data={data}
          onFilterChange={handleFilterChange}
          placeholder="Filter by exchanges..."
        />
      </div>
      <div className="exchanges-table-wrapper">
        <table className="exchanges-table">
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col.key} onClick={() => handleSort(col.key)} className="exchanges-sortable">
                  {col.label}
                  {sortConfig.key === col.key && (
                    <span className="exchanges-sort-indicator">
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, index) => (
              <tr
                key={`${row.exchange}-${index}`}
                className={isFiltering ? 'filtering-in' : ''}
              >
                {columns.map(col => (
                  <td key={col.key}>
                    {col.format ? col.format(row[col.key], row, dynamicTotals) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ExchangesDashboard = () => {
  // State for all data
  const [futuresData, setFuturesData] = useState([]);
  const [spotData, setSpotData] = useState([]);
  const [futuresHistorical, setFuturesHistorical] = useState([]);
  const [spotHistorical, setSpotHistorical] = useState([]);
  const [oiHistorical, setOiHistorical] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for chart controls
  const [futuresVolumeChart, setFuturesVolumeChart] = useState({ type: 'usd', timeframe: '30d' });
  const [futuresOiChart, setFuturesOiChart] = useState({ type: 'usd', timeframe: '30d' });
  const [spotVolumeChart, setSpotVolumeChart] = useState({ type: 'usd', timeframe: '30d' });

  // State for legend selections - separate for area/line charts and bar charts
  const [selectedFuturesExchanges, setSelectedFuturesExchanges] = useState([]);
  const [selectedSpotExchanges, setSelectedSpotExchanges] = useState([]);
  const [selectedOiExchanges, setSelectedOiExchanges] = useState([]);

  // State for bar chart legend selections (separate from area/line charts)
  const [selectedFuturesVolumeBarExchanges, setSelectedFuturesVolumeBarExchanges] = useState([]);
  const [selectedOiBarExchanges, setSelectedOiBarExchanges] = useState([]);
  const [selectedSpotVolumeBarExchanges, setSelectedSpotVolumeBarExchanges] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [futuresResponse, spotResponse, futuresHistResponse, spotHistResponse, oiHistResponse] = await Promise.all([
          fetch(`${base_api_url}/api/data/cex-futures`),
          fetch(`${base_api_url}/api/data/cex-spot`),
          fetch(`${base_api_url}/api/data/cex-futures-historical`),
          fetch(`${base_api_url}/api/data/cex-spot-historical`),
          fetch(`${base_api_url}/api/data/cex-historical-oi`)
        ]);

        if (!futuresResponse.ok || !spotResponse.ok || !futuresHistResponse.ok || !spotHistResponse.ok || !oiHistResponse.ok) {
          throw new Error('Failed to fetch data');
        }

        const futuresData = await futuresResponse.json();
        const spotData = await spotResponse.json();
        const futuresHistData = await futuresHistResponse.json();
        const spotHistData = await spotHistResponse.json();
        const oiHistData = await oiHistResponse.json();

        setFuturesData(futuresData);
        setSpotData(spotData);
        setFuturesHistorical(futuresHistData);
        setSpotHistorical(spotHistData);
        setOiHistorical(oiHistData);

        // Initialize selected exchanges
        const futuresExchanges = Object.keys(futuresHistData[0] || {}).filter(key => key !== 'date');
        const spotExchanges = Object.keys(spotHistData[0] || {}).filter(key => key !== 'date');
        const oiExchanges = Object.keys(oiHistData[0] || {}).filter(key => key !== 'date');

        setSelectedFuturesExchanges(futuresExchanges);
        setSelectedSpotExchanges(spotExchanges);
        setSelectedOiExchanges(oiExchanges);

        // Initialize bar chart legend states with actual futures/spot data exchanges
        const futuresDataExchanges = futuresData.map(item => item.exchange).filter(exchange => exchange && exchange.toLowerCase() !== 'total');
        const spotDataExchanges = spotData.map(item => item.exchange).filter(exchange => exchange && exchange.toLowerCase() !== 'total');

        setSelectedFuturesVolumeBarExchanges(futuresDataExchanges);
        setSelectedOiBarExchanges(futuresDataExchanges);
        setSelectedSpotVolumeBarExchanges(spotDataExchanges);

        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter data by timeframe
  const filterByTimeframe = (data, timeframe) => {
    if (!data.length) return [];

    const days = timeframe === '7d' ? 7 : timeframe === '14d' ? 14 : timeframe === '30d' ? 30 : 365;
    return data.slice(-days);
  };

  // Transform data for charts
  const transformForChart = (data, selectedExchanges, chartType, timeframe) => {
    const filteredData = filterByTimeframe(data, timeframe);

    return filteredData.map(row => {
      const newRow = { date: formatDate(row.date) };

      if (chartType === 'percent') {
        const total = selectedExchanges.reduce((sum, exchange) => sum + (parseFloat(row[exchange]) || 0), 0);
        selectedExchanges.forEach(exchange => {
          newRow[exchange] = total > 0 ? ((parseFloat(row[exchange]) || 0) / total) * 100 : 0;
        });
      } else {
        selectedExchanges.forEach(exchange => {
          newRow[exchange] = parseFloat(row[exchange]) || 0;
        });
      }

      return newRow;
    });
  };

  // Calculate X-axis tick interval based on timeframe and data length
  const getXAxisInterval = (dataLength, timeframe) => {
    let targetTicks;
    switch (timeframe) {
      case '7d': targetTicks = 7; break;
      case '14d': targetTicks = 7; break;
      case '30d': targetTicks = 4; break;
      case '365d': targetTicks = 12; break;
      default: targetTicks = 7;
    }

    if (dataLength <= targetTicks) return 0;
    return Math.floor(dataLength / targetTicks) - 1;
  };

  // Prepare chart data
  const futuresVolumeChartData = transformForChart(
    futuresHistorical,
    selectedFuturesExchanges,
    futuresVolumeChart.type,
    futuresVolumeChart.timeframe
  );

  const spotVolumeChartData = transformForChart(
    spotHistorical,
    selectedSpotExchanges,
    spotVolumeChart.type,
    spotVolumeChart.timeframe
  );

  const oiChartData = transformForChart(
    oiHistorical,
    selectedOiExchanges,
    futuresOiChart.type,
    futuresOiChart.timeframe
  );

  // Prepare bar chart data
  const prepareBarChartData = (data, selectedExchanges) => {
    return selectedExchanges.map(exchange => {
      const exchangeData = data.find(item => item.exchange === exchange) || {};
      return {
        exchange: getExchangeDisplayName(exchange),
        originalExchange: exchange, // Keep original for color mapping
        value: parseFloat(exchangeData.total_volume_usd || exchangeData.total_open_interest_usd || 0)
      };
    }).sort((a, b) => b.value - a.value);
  };

  // Get all available exchanges for bar chart legend initialization (filter out TOTAL)
  const getAllFuturesBarExchanges = () => futuresData.map(item => item.exchange).filter(exchange => exchange && exchange.toLowerCase() !== 'total');
  const getAllSpotBarExchanges = () => spotData.map(item => item.exchange).filter(exchange => exchange && exchange.toLowerCase() !== 'total');

  // Filter out TOTAL entries from data
  const filteredFuturesData = futuresData.filter(item => item.exchange && item.exchange.toLowerCase() !== 'total');
  const filteredSpotData = spotData.filter(item => item.exchange && item.exchange.toLowerCase() !== 'total');

  // Calculate totals for percentage calculations
  const totalFuturesVolume = filteredFuturesData.reduce((sum, item) => sum + (parseFloat(item.total_volume_usd) || 0), 0);
  const totalFuturesOI = filteredFuturesData.reduce((sum, item) => sum + (parseFloat(item.total_open_interest_usd) || 0), 0);
  const totalSpotVolume = filteredSpotData.reduce((sum, item) => sum + (parseFloat(item.total_volume_usd) || 0), 0);

  // Get all available exchanges for market share chart legends
  const getAllFuturesHistoricalExchanges = () => Object.keys(futuresHistorical[0] || {}).filter(key => key !== 'date');
  const getAllSpotHistoricalExchanges = () => Object.keys(spotHistorical[0] || {}).filter(key => key !== 'date');
  const getAllOiHistoricalExchanges = () => Object.keys(oiHistorical[0] || {}).filter(key => key !== 'date');

  // Legend items - now showing all available exchanges for consistent behavior
  const futuresLegendItems = getAllFuturesHistoricalExchanges().map(exchange => ({
    key: exchange,
    name: getExchangeDisplayName(exchange),
    color: EXCHANGE_COLORS[exchange] || '#8884d8'
  }));

  const spotLegendItems = getAllSpotHistoricalExchanges().map(exchange => ({
    key: exchange,
    name: getExchangeDisplayName(exchange),
    color: EXCHANGE_COLORS[exchange] || '#8884d8'
  }));

  const oiLegendItems = getAllOiHistoricalExchanges().map(exchange => ({
    key: exchange,
    name: getExchangeDisplayName(exchange),
    color: EXCHANGE_COLORS[exchange] || '#8884d8'
  }));

  // Bar chart legend items (separate from area/line chart legends)
  const futuresVolumeBarLegendItems = getAllFuturesBarExchanges().map(exchange => ({
    key: exchange,
    name: getExchangeDisplayName(exchange),
    color: EXCHANGE_COLORS[exchange] || '#8884d8'
  }));

  const oiBarLegendItems = getAllFuturesBarExchanges().map(exchange => ({
    key: exchange,
    name: getExchangeDisplayName(exchange),
    color: EXCHANGE_COLORS[exchange] || '#8884d8'
  }));

  const spotVolumeBarLegendItems = getAllSpotBarExchanges().map(exchange => ({
    key: exchange,
    name: getExchangeDisplayName(exchange),
    color: EXCHANGE_COLORS[exchange] || '#8884d8'
  }));

  // Table columns
  const futuresColumns = [
    { key: 'exchange', label: 'Exchange', format: (val) => getExchangeDisplayName(val) },
    { key: 'total_volume_usd', label: 'Volume (USD)', format: formatCurrency },
    {
      key: 'volume_percentage',
      label: '% of Volume',
      format: (val, row, dynamicTotals) => {
        const volume = parseFloat(row.total_volume_usd) || 0;
        const totalVolume = dynamicTotals ? dynamicTotals.totalVolume : totalFuturesVolume;
        const percentage = totalVolume > 0 ? (volume / totalVolume) * 100 : 0;
        return formatPercentage(percentage);
      }
    },
    { key: 'total_open_interest_usd', label: 'Open Interest (USD)', format: formatCurrency },
    {
      key: 'oi_percentage',
      label: '% of OI',
      format: (val, row, dynamicTotals) => {
        const oi = parseFloat(row.total_open_interest_usd) || 0;
        const totalOI = dynamicTotals ? dynamicTotals.totalOI : totalFuturesOI;
        const percentage = totalOI > 0 ? (oi / totalOI) * 100 : 0;
        return formatPercentage(percentage);
      }
    },
    {
      key: 'instrument_count', label: 'Instruments', format: (val) => {
        const numVal = parseInt(val);
        return isNaN(numVal) ? '0' : numVal.toLocaleString();
      }
    },
    { key: 'exchange_trade_volume_24h_btc', label: 'Volume (BTC)', format: formatNumber }
  ];

  const spotColumns = [
    { key: 'exchange', label: 'Exchange', format: (val) => getExchangeDisplayName(val) },
    { key: 'total_volume_usd', label: 'Volume (USD)', format: formatCurrency },
    {
      key: 'volume_percentage',
      label: '% of Volume',
      format: (val, row, dynamicTotals) => {
        const volume = parseFloat(row.total_volume_usd) || 0;
        const totalVolume = dynamicTotals ? dynamicTotals.totalVolume : totalSpotVolume;
        const percentage = totalVolume > 0 ? (volume / totalVolume) * 100 : 0;
        return formatPercentage(percentage);
      }
    },
    { key: 'exchange_trade_volume_24h_btc', label: 'Volume (BTC)', format: formatNumber }
  ];

  if (loading) return <div className="exchanges-loading">Loading exchange data...</div>;
  if (error) return <div className="exchanges-error">Error loading data: {error}</div>;

  return (
    <div className="exchanges-dashboard">
      <h1 className="exchanges-title">Exchanges</h1>

      {/* Futures Section */}
      <section className="exchanges-section">
        <h2 className="exchanges-section-title">Futures</h2>

        {/* Volume Market Share Chart */}
        <div className="exchanges-chart-container">
          <h3 className="exchanges-chart-title">Futures Volume Market Share</h3>
          <ChartControls
            chartType={futuresVolumeChart.type}
            onChartTypeChange={(type) => setFuturesVolumeChart(prev => ({ ...prev, type }))}
            timeframe={futuresVolumeChart.timeframe}
            onTimeframeChange={(timeframe) => setFuturesVolumeChart(prev => ({ ...prev, timeframe }))}
          />
          <div className="exchanges-chart-wrapper">
            <ResponsiveContainer width="100%" height={400}>
              {futuresVolumeChart.type === 'line' ? (
                <LineChart data={futuresVolumeChartData}>
                  <XAxis
                    dataKey="date"
                    interval={getXAxisInterval(futuresVolumeChartData.length, futuresVolumeChart.timeframe)}
                    strokeWidth={0.5}
                    stroke="black"
                    tickFormatter={(str) => {
                      const date = new Date(str);
                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }}
                  />
                  <YAxis tickFormatter={formatCurrency} strokeWidth={0.5} stroke="black" />
                  <Tooltip content={<CustomTooltip chartType={futuresVolumeChart.type} />} />
                  {selectedFuturesExchanges.map(exchange => (
                    <Line
                      key={exchange}
                      type="linear"
                      dataKey={exchange}
                      stroke={EXCHANGE_COLORS[exchange] || '#8884d8'}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              ) : (
                <AreaChart data={futuresVolumeChartData} stackOffset={futuresVolumeChart.type === 'percent' ? 'expand' : undefined}>
                  <XAxis
                    dataKey="date"
                    interval={getXAxisInterval(futuresVolumeChartData.length, futuresVolumeChart.timeframe)}
                    strokeWidth={0.5}
                    stroke="black"
                    tickFormatter={(str) => {
                      const date = new Date(str);
                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }}
                  />
                  <YAxis tickFormatter={futuresVolumeChart.type === 'percent' ? (val) => `${(val * 100).toFixed(0)}%` : formatCurrency} strokeWidth={0.5} stroke="black" />
                  <Tooltip content={<CustomTooltip chartType={futuresVolumeChart.type} />} />
                  {selectedFuturesExchanges.map(exchange => (
                    <Area
                      key={exchange}
                      type="linear"
                      dataKey={exchange}
                      stackId="1"
                      fill={EXCHANGE_COLORS[exchange] || '#8884d8'}
                      stroke={EXCHANGE_COLORS[exchange] || '#8884d8'}
                    />
                  ))}
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
          <InteractiveLegend
            items={futuresLegendItems}
            selectedItems={selectedFuturesExchanges}
            onToggle={(exchange) => {
              setSelectedFuturesExchanges(prev =>
                prev.includes(exchange)
                  ? prev.filter(e => e !== exchange)
                  : [...prev, exchange]
              );
            }}
            onToggleAll={() => {
              const allExchanges = getAllFuturesHistoricalExchanges();
              setSelectedFuturesExchanges(
                selectedFuturesExchanges.length === allExchanges.length ? [] : allExchanges
              );
            }}
            allItems={futuresLegendItems}
          />
        </div>

        {/* Futures Data Table */}
        <DataTable
          data={filteredFuturesData}
          columns={futuresColumns}
          title="Futures Market Data"
        />

        {/* Volume Ranking Bar Chart */}
        <div className="exchanges-bar-chart-container">
          <h3 className="exchanges-chart-title">Futures Volume Ranking</h3>
          <div className="exchanges-bar-chart-wrapper">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={prepareBarChartData(filteredFuturesData, selectedFuturesVolumeBarExchanges)}
                margin={{ top: 20, right: 20, left: 10, bottom: 20 }}
                barGap={8}
                maxBarSize={40}
              >
                <XAxis
                  dataKey="exchange"
                  tickLine={true}
                  strokeWidth={0.5}
                  stroke="black"
                  tick={{
                    fill: '#64748b',
                    fontSize: 10,
                    fontFamily: 'roboto mono, system-ui, sans-serif',
                    angle: -45,
                    textAnchor: 'end'
                  }}
                  height={80}
                />
                <YAxis
                  tickFormatter={formatCurrency}
                  tickLine={true}
                  strokeWidth={0.5}
                  stroke="black"
                  tick={{
                    fill: '#64748b',
                    fontSize: 10,
                    fontFamily: 'roboto mono, system-ui, sans-serif'
                  }}
                />
                <Tooltip content={<CustomBarTooltip chartContext="volume" totalValue={totalFuturesVolume} />} cursor={false} />
                <Bar
                  dataKey="value"
                  fillOpacity={0.9}
                  radius={[2, 2, 0, 0]}
                  animationDuration={600}
                  animationEasing="ease-out"
                  label={<CustomLabel />}
                >
                  {prepareBarChartData(filteredFuturesData, selectedFuturesVolumeBarExchanges).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={EXCHANGE_COLORS[entry.originalExchange] || '#3033d9'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <InteractiveLegend
            items={futuresVolumeBarLegendItems}
            selectedItems={selectedFuturesVolumeBarExchanges}
            onToggle={(exchange) => {
              setSelectedFuturesVolumeBarExchanges(prev =>
                prev.includes(exchange)
                  ? prev.filter(e => e !== exchange)
                  : [...prev, exchange]
              );
            }}
            onToggleAll={() => {
              const allExchanges = getAllFuturesBarExchanges();
              setSelectedFuturesVolumeBarExchanges(
                selectedFuturesVolumeBarExchanges.length === allExchanges.length ? [] : allExchanges
              );
            }}
            allItems={futuresVolumeBarLegendItems}
          />
        </div>

        {/* OI Ranking Bar Chart */}
        <div className="exchanges-bar-chart-container">
          <h3 className="exchanges-chart-title">Open Interest Ranking</h3>
          <div className="exchanges-bar-chart-wrapper">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={prepareBarChartData(filteredFuturesData.map(item => ({
                  ...item,
                  total_volume_usd: item.total_open_interest_usd
                })), selectedOiBarExchanges)}
                margin={{ top: 20, right: 20, left: 10, bottom: 20 }}
                barGap={8}
                maxBarSize={40}
              >
                <XAxis
                  dataKey="exchange"
                  tickLine={true}
                  strokeWidth={0.5}
                  stroke="black"
                  tick={{
                    fill: '#64748b',
                    fontSize: 10,
                    fontFamily: 'roboto mono, system-ui, sans-serif',
                    angle: -45,
                    textAnchor: 'end'
                  }}
                  height={80}
                />
                <YAxis
                  tickFormatter={formatCurrency}
                  tickLine={true}
                  strokeWidth={0.5}
                  stroke="black"
                  tick={{
                    fill: '#64748b',
                    fontSize: 10,
                    fontFamily: 'roboto mono, system-ui, sans-serif'
                  }}
                />
                <Tooltip content={<CustomBarTooltip chartContext="OI" totalValue={totalFuturesOI} />} cursor={false} />
                <Bar
                  dataKey="value"
                  fillOpacity={0.9}
                  radius={[2, 2, 0, 0]}
                  animationDuration={600}
                  animationEasing="ease-out"
                  label={<CustomLabel />}
                >
                  {prepareBarChartData(filteredFuturesData.map(item => ({
                    ...item,
                    total_volume_usd: item.total_open_interest_usd
                  })), selectedOiBarExchanges).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={EXCHANGE_COLORS[entry.originalExchange] || '#3033d9'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <InteractiveLegend
            items={oiBarLegendItems}
            selectedItems={selectedOiBarExchanges}
            onToggle={(exchange) => {
              setSelectedOiBarExchanges(prev =>
                prev.includes(exchange)
                  ? prev.filter(e => e !== exchange)
                  : [...prev, exchange]
              );
            }}
            onToggleAll={() => {
              const allExchanges = getAllFuturesBarExchanges();
              setSelectedOiBarExchanges(
                selectedOiBarExchanges.length === allExchanges.length ? [] : allExchanges
              );
            }}
            allItems={oiBarLegendItems}
          />
        </div>

        {/* OI Market Share Chart */}
        <div className="exchanges-chart-container">
          <h3 className="exchanges-chart-title">Open Interest Market Share</h3>
          <ChartControls
            chartType={futuresOiChart.type}
            onChartTypeChange={(type) => setFuturesOiChart(prev => ({ ...prev, type }))}
            timeframe={futuresOiChart.timeframe}
            onTimeframeChange={(timeframe) => setFuturesOiChart(prev => ({ ...prev, timeframe }))}
          />
          <div className="exchanges-chart-wrapper">
            <ResponsiveContainer width="100%" height={400}>
              {futuresOiChart.type === 'line' ? (
                <LineChart data={oiChartData}>
                  <XAxis
                    dataKey="date"
                    interval={getXAxisInterval(oiChartData.length, futuresOiChart.timeframe)}
                    strokeWidth={0.5}
                    stroke="black"
                    tickFormatter={(str) => {
                      const date = new Date(str);
                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }}
                  />
                  <YAxis tickFormatter={formatCurrency} strokeWidth={0.5} stroke="black" />
                  <Tooltip content={<CustomTooltip chartType={futuresOiChart.type} />} />
                  {selectedOiExchanges.map(exchange => (
                    <Line
                      key={exchange}
                      type="linear"
                      dataKey={exchange}
                      stroke={EXCHANGE_COLORS[exchange] || '#8884d8'}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              ) : (
                <AreaChart data={oiChartData} stackOffset={futuresOiChart.type === 'percent' ? 'expand' : undefined}>
                  <XAxis
                    dataKey="date"
                    interval={getXAxisInterval(oiChartData.length, futuresOiChart.timeframe)}
                    strokeWidth={0.5}
                    stroke="black"
                    tickFormatter={(str) => {
                      const date = new Date(str);
                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }}
                  />
                  <YAxis tickFormatter={futuresOiChart.type === 'percent' ? (val) => `${(val * 100).toFixed(0)}%` : formatCurrency} strokeWidth={0.5} stroke="black" />
                  <Tooltip content={<CustomTooltip chartType={futuresOiChart.type} />} />
                  {selectedOiExchanges.map(exchange => (
                    <Area
                      key={exchange}
                      type="linear"
                      dataKey={exchange}
                      stackId="1"
                      fill={EXCHANGE_COLORS[exchange] || '#8884d8'}
                      stroke={EXCHANGE_COLORS[exchange] || '#8884d8'}
                    />
                  ))}
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
          <InteractiveLegend
            items={oiLegendItems}
            selectedItems={selectedOiExchanges}
            onToggle={(exchange) => {
              setSelectedOiExchanges(prev =>
                prev.includes(exchange)
                  ? prev.filter(e => e !== exchange)
                  : [...prev, exchange]
              );
            }}
            onToggleAll={() => {
              const allExchanges = getAllOiHistoricalExchanges();
              setSelectedOiExchanges(
                selectedOiExchanges.length === allExchanges.length ? [] : allExchanges
              );
            }}
            allItems={oiLegendItems}
          />
        </div>
      </section>

      {/* Spot Section */}
      <section className="exchanges-section">
        <h2 className="exchanges-section-title">Spot</h2>

        {/* Spot Volume Market Share Chart */}
        <div className="exchanges-chart-container">
          <h3 className="exchanges-chart-title">Spot Volume Market Share</h3>
          <ChartControls
            chartType={spotVolumeChart.type}
            onChartTypeChange={(type) => setSpotVolumeChart(prev => ({ ...prev, type }))}
            timeframe={spotVolumeChart.timeframe}
            onTimeframeChange={(timeframe) => setSpotVolumeChart(prev => ({ ...prev, timeframe }))}
          />
          <div className="exchanges-chart-wrapper">
            <ResponsiveContainer width="100%" height={400}>
              {spotVolumeChart.type === 'line' ? (
                <LineChart data={spotVolumeChartData}>
                  <XAxis
                    dataKey="date"
                    interval={getXAxisInterval(spotVolumeChartData.length, spotVolumeChart.timeframe)}
                    strokeWidth={0.5}
                    stroke="black"
                    tickFormatter={(str) => {
                      const date = new Date(str);
                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }}
                  />
                  <YAxis tickFormatter={formatCurrency} strokeWidth={0.5} stroke="black" />
                  <Tooltip content={<CustomTooltip chartType={spotVolumeChart.type} />} />
                  {selectedSpotExchanges.map(exchange => (
                    <Line
                      key={exchange}
                      type="linear"
                      dataKey={exchange}
                      stroke={EXCHANGE_COLORS[exchange] || '#8884d8'}
                      strokeWidth={1}
                      dot={false}
                    />
                  ))}
                </LineChart>
              ) : (
                <AreaChart data={spotVolumeChartData} stackOffset={spotVolumeChart.type === 'percent' ? 'expand' : undefined}>
                  <XAxis
                    dataKey="date"
                    interval={getXAxisInterval(spotVolumeChartData.length, spotVolumeChart.timeframe)}
                    strokeWidth={0.5}
                    stroke="black"
                    tickFormatter={(str) => {
                      const date = new Date(str);
                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }}
                  />
                  <YAxis tickFormatter={spotVolumeChart.type === 'percent' ? (val) => `${(val * 100).toFixed(0)}%` : formatCurrency} strokeWidth={0.5} stroke="black" />
                  <Tooltip content={<CustomTooltip chartType={spotVolumeChart.type} />} />
                  {selectedSpotExchanges.map(exchange => (
                    <Area
                      key={exchange}
                      type="linear"
                      dataKey={exchange}
                      stackId="1"
                      fill={EXCHANGE_COLORS[exchange] || '#8884d8'}
                      stroke={EXCHANGE_COLORS[exchange] || '#8884d8'}
                    />
                  ))}
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
          <InteractiveLegend
            items={spotLegendItems}
            selectedItems={selectedSpotExchanges}
            onToggle={(exchange) => {
              setSelectedSpotExchanges(prev =>
                prev.includes(exchange)
                  ? prev.filter(e => e !== exchange)
                  : [...prev, exchange]
              );
            }}
            onToggleAll={() => {
              const allExchanges = getAllSpotHistoricalExchanges();
              setSelectedSpotExchanges(
                selectedSpotExchanges.length === allExchanges.length ? [] : allExchanges
              );
            }}
            allItems={spotLegendItems}
          />
        </div>

        {/* Spot Data Table */}
        <DataTable
          data={filteredSpotData}
          columns={spotColumns}
          title="Spot Market Data"
        />

        {/* Spot Volume Ranking Bar Chart */}
        <div className="exchanges-bar-chart-container">
          <h3 className="exchanges-chart-title">Spot Volume Ranking</h3>
          <div className="exchanges-bar-chart-wrapper">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={prepareBarChartData(filteredSpotData, selectedSpotVolumeBarExchanges)}
                margin={{ top: 20, right: 20, left: 10, bottom: 20 }}
                barGap={8}
                maxBarSize={40}
              >
                <XAxis
                  dataKey="exchange"
                  tickLine={true}
                  strokeWidth={0.5}
                  stroke="black"
                  tick={{
                    fill: '#64748b',
                    fontSize: 10,
                    fontFamily: 'roboto mono, system-ui, sans-serif',
                    angle: -45,
                    textAnchor: 'end'
                  }}
                  height={80}
                />
                <YAxis
                  tickFormatter={formatCurrency}
                  tickLine={true}
                  strokeWidth={0.5}
                  stroke="black"
                  tick={{
                    fill: '#64748b',
                    fontSize: 10,
                    fontFamily: 'roboto mono, system-ui, sans-serif'
                  }}
                />
                <Tooltip content={<CustomBarTooltip chartContext="volume" totalValue={totalSpotVolume} />} cursor={false} />
                <Bar
                  dataKey="value"
                  fillOpacity={0.9}
                  radius={[2, 2, 0, 0]}
                  animationDuration={600}
                  animationEasing="ease-out"
                  label={<CustomLabel />}
                >
                  {prepareBarChartData(filteredSpotData, selectedSpotVolumeBarExchanges).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={EXCHANGE_COLORS[entry.originalExchange] || '#3033d9'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <InteractiveLegend
            items={spotVolumeBarLegendItems}
            selectedItems={selectedSpotVolumeBarExchanges}
            onToggle={(exchange) => {
              setSelectedSpotVolumeBarExchanges(prev =>
                prev.includes(exchange)
                  ? prev.filter(e => e !== exchange)
                  : [...prev, exchange]
              );
            }}
            onToggleAll={() => {
              const allExchanges = getAllSpotBarExchanges();
              setSelectedSpotVolumeBarExchanges(
                selectedSpotVolumeBarExchanges.length === allExchanges.length ? [] : allExchanges
              );
            }}
            allItems={spotVolumeBarLegendItems}
          />
        </div>
      </section>
    </div>
  );
};

export default ExchangesDashboard; 